import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogIn, Zap, Eye, EyeOff } from "lucide-react";

function AnimatedDiagram() {
  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <svg viewBox="0 0 480 420" className="w-full max-w-[420px] mx-auto" fill="none">
        {/* Animated connection lines */}
        <path d="M240 75 L130 175" stroke="white" strokeWidth="2" strokeOpacity="0.25" strokeDasharray="6 4">
          <animate attributeName="stroke-dashoffset" values="20;0" dur="2s" repeatCount="indefinite" />
        </path>
        <path d="M240 75 L350 175" stroke="white" strokeWidth="2" strokeOpacity="0.25" strokeDasharray="6 4">
          <animate attributeName="stroke-dashoffset" values="20;0" dur="2.2s" repeatCount="indefinite" />
        </path>
        <path d="M130 210 L200 290" stroke="white" strokeWidth="2" strokeOpacity="0.2" strokeDasharray="6 4">
          <animate attributeName="stroke-dashoffset" values="20;0" dur="1.8s" repeatCount="indefinite" />
        </path>
        <path d="M350 210 L280 290" stroke="white" strokeWidth="2" strokeOpacity="0.2" strokeDasharray="6 4">
          <animate attributeName="stroke-dashoffset" values="20;0" dur="2.5s" repeatCount="indefinite" />
        </path>
        <path d="M240 325 L240 370" stroke="white" strokeWidth="2" strokeOpacity="0.15" strokeDasharray="6 4">
          <animate attributeName="stroke-dashoffset" values="10;0" dur="1.5s" repeatCount="indefinite" />
        </path>

        {/* Animated flow dots on lines */}
        <circle r="3" fill="white" opacity="0.6">
          <animateMotion dur="2s" repeatCount="indefinite" path="M240 75 L130 175" />
        </circle>
        <circle r="3" fill="white" opacity="0.6">
          <animateMotion dur="2.2s" repeatCount="indefinite" path="M240 75 L350 175" />
        </circle>
        <circle r="3" fill="#A5B4FC" opacity="0.7">
          <animateMotion dur="1.8s" repeatCount="indefinite" path="M130 210 L200 290" />
        </circle>
        <circle r="3" fill="#A5B4FC" opacity="0.7">
          <animateMotion dur="2.5s" repeatCount="indefinite" path="M350 210 L280 290" />
        </circle>

        {/* Arrow markers */}
        <defs>
          <filter id="glow">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {/* Card 1 — Top: "Entreprise" */}
        <g filter="url(#glow)">
          <rect x="175" y="40" width="130" height="52" rx="12" fill="white" fillOpacity="0.12" stroke="white" strokeWidth="1.2" strokeOpacity="0.3">
            <animate attributeName="fill-opacity" values="0.12;0.18;0.12" dur="3s" repeatCount="indefinite" />
          </rect>
          <circle cx="195" cy="58" r="5" fill="#818CF8" />
          <text x="207" y="62" fill="white" fontSize="10" fontWeight="700" fontFamily="system-ui">Mon Projet SaaS</text>
          <text x="195" y="80" fill="white" fillOpacity="0.45" fontSize="7" fontFamily="system-ui">ENTREPRISE</text>
        </g>

        {/* Card 2 — Left: "Frontend" */}
        <g>
          <rect x="65" y="165" width="130" height="52" rx="12" fill="white" fillOpacity="0.10" stroke="white" strokeWidth="1" strokeOpacity="0.2">
            <animate attributeName="fill-opacity" values="0.10;0.16;0.10" dur="3.5s" repeatCount="indefinite" />
          </rect>
          <circle cx="85" cy="183" r="5" fill="#34D399" />
          <text x="97" y="187" fill="white" fontSize="9.5" fontWeight="600" fontFamily="system-ui">Landing Page</text>
          <text x="85" y="205" fill="white" fillOpacity="0.35" fontSize="7" fontFamily="system-ui">frontend</text>
        </g>

        {/* Card 3 — Right: "Backend" */}
        <g>
          <rect x="285" y="165" width="130" height="52" rx="12" fill="white" fillOpacity="0.10" stroke="white" strokeWidth="1" strokeOpacity="0.2">
            <animate attributeName="fill-opacity" values="0.10;0.15;0.10" dur="4s" repeatCount="indefinite" />
          </rect>
          <circle cx="305" cy="183" r="5" fill="#60A5FA" />
          <text x="317" y="187" fill="white" fontSize="9.5" fontWeight="600" fontFamily="system-ui">API Gateway</text>
          <text x="305" y="205" fill="white" fillOpacity="0.35" fontSize="7" fontFamily="system-ui">backend</text>
        </g>

        {/* Card 4 — Center: Decision node */}
        <g>
          <rect x="175" y="275" width="130" height="56" rx="12" fill="white" fillOpacity="0.10" stroke="#F472B6" strokeWidth="1" strokeOpacity="0.35">
            <animate attributeName="stroke-opacity" values="0.35;0.6;0.35" dur="2s" repeatCount="indefinite" />
          </rect>
          <text x="195" y="300" fill="white" fontSize="9" fontWeight="600" fontFamily="system-ui">Validation</text>
          <text x="195" y="318" fill="#F9A8D4" fillOpacity="0.6" fontSize="7" fontFamily="system-ui">Résultat conforme ?</text>
        </g>

        {/* Card 5 — Bottom: KPI */}
        <g>
          <rect x="190" y="365" width="100" height="40" rx="10" fill="white" fillOpacity="0.08" stroke="white" strokeWidth="0.8" strokeOpacity="0.15">
            <animate attributeName="fill-opacity" values="0.08;0.14;0.08" dur="2.5s" repeatCount="indefinite" />
          </rect>
          <text x="215" y="385" fill="#34D399" fontSize="14" fontWeight="800" fontFamily="system-ui" textAnchor="middle">99.9%</text>
          <text x="215" y="398" fill="white" fillOpacity="0.35" fontSize="7" fontFamily="system-ui" textAnchor="middle">Uptime SLA</text>
        </g>

        {/* Decorative tags */}
        <rect x="70" y="232" width="42" height="14" rx="7" fill="#818CF8" fillOpacity="0.2" />
        <text x="77" y="242" fill="#C7D2FE" fontSize="6.5" fontFamily="system-ui">react</text>

        <rect x="120" y="232" width="55" height="14" rx="7" fill="#818CF8" fillOpacity="0.2" />
        <text x="126" y="242" fill="#C7D2FE" fontSize="6.5" fontFamily="system-ui">typescript</text>

        <rect x="290" y="232" width="36" height="14" rx="7" fill="#60A5FA" fillOpacity="0.2" />
        <text x="296" y="242" fill="#93C5FD" fontSize="6.5" fontFamily="system-ui">api</text>

        <rect x="332" y="232" width="52" height="14" rx="7" fill="#60A5FA" fillOpacity="0.2" />
        <text x="338" y="242" fill="#93C5FD" fontSize="6.5" fontFamily="system-ui">FastAPI</text>
      </svg>
    </div>
  );
}

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = await login(email, password);
    if (result.success) {
      navigate("/dashboard");
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Left - Animated diagram showcase */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-indigo-600 via-indigo-700 to-blue-800 relative overflow-hidden">
        {/* Background grid pattern */}
        <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
        {/* Animated card diagram */}
        <AnimatedDiagram />
        {/* Bottom-left text overlay */}
        <div className="absolute bottom-12 left-12 z-10">
          <h2 className="font-heading text-3xl font-bold text-white tracking-tight leading-tight mb-2">
            Construisez vos<br />diagrammes<br />d'entreprise
          </h2>
          <p className="text-white/40 text-sm max-w-xs leading-relaxed">
            Organigrammes, scénarios conditionnels, processus métier. Tout en drag & drop.
          </p>
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <h1 className="font-heading text-3xl font-bold tracking-tight text-gray-900 mb-1">
            CardFlow
          </h1>
          <p className="text-sm text-gray-400 mb-8">
            Connexion à votre espace
          </p>

          {error && (
            <div
              className="bg-rose-50 border border-rose-200 rounded-lg p-3 mb-5 text-sm text-rose-600"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          {/* Quick login */}
          <button
            type="button"
            onClick={async () => {
              setEmail("admin@example.com");
              setPassword("admin123");
              setError("");
              setLoading(true);
              const result = await login("admin@example.com", "admin123");
              if (result.success) {
                navigate("/dashboard");
              } else {
                setError(result.error);
              }
              setLoading(false);
            }}
            disabled={loading}
            className="mb-5 w-full bg-gray-900 text-white px-4 py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 hover:bg-gray-800 transition-colors shadow-sm disabled:opacity-50"
            data-testid="quick-login-btn"
          >
            <Zap size={14} /> {loading ? "Connexion..." : "Connexion rapide (Admin)"}
          </button>

          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400">ou</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-semibold text-gray-500">
                Email
              </Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="rounded-lg border-gray-200"
                data-testid="login-email-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-semibold text-gray-500">
                Mot de passe
              </Label>
              <div className="relative">
                <Input
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="rounded-lg border-gray-200 pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  data-testid="toggle-password-visibility"
                  tabIndex={-1}
                >
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="bg-indigo-600 text-white px-4 py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 hover:bg-indigo-700 transition-colors disabled:opacity-50 shadow-sm mt-1"
              data-testid="login-submit-btn"
            >
              <LogIn size={16} />
              {loading ? "Connexion..." : "Se connecter"}
            </button>
          </form>

          <p className="mt-6 text-sm text-center text-gray-400">
            Pas encore de compte ?{" "}
            <Link
              to="/register"
              className="text-indigo-600 font-semibold hover:text-indigo-700"
              data-testid="register-link"
            >
              S'inscrire
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
